import { C1C_DEVICE_TYPES } from '@/shared/config';
import { formatC1cDeviceLabel } from '@/shared/lib';

type C1cDeviceType = (typeof C1C_DEVICE_TYPES)[number];

interface CardioOneFacility {
  id: string;
  isCardioOne?: boolean;
}

interface CardioOneStudy {
  externalSource?: { provider?: string; externalId?: string } | null;
  facility?: { id: string } | null;
}

const C1C_PROVIDER = 'c1c';

/**
 * Tells whether the signed-in user has at least one facility mapped to CardioOne.
 *
 * @param supportFacilities - Facilities from the current user store.
 * @returns True when any facility is flagged `isCardioOne`.
 */
const hasCardioOneFacilities = (supportFacilities: { isCardioOne?: boolean }[]) =>
  supportFacilities.some((facility) => facility.isCardioOne === true);

/**
 * Tells whether a facility is currently mapped to CardioOne.
 *
 * @param facilityId - Facility to look up.
 * @param supportFacilities - Facilities from the current user store.
 * @returns True when the facility is found and flagged `isCardioOne`.
 */
const isCardioOneFacility = (
  facilityId: string | undefined,
  supportFacilities: CardioOneFacility[],
) =>
  supportFacilities.some((facility) => facility.id === facilityId && facility.isCardioOne === true);

/**
 * Tells whether a study originates from CardioOne. Reads only the study's external source provider, ignoring the facility's `isCardioOne` flag.
 *
 * @param study - Study with its external source.
 * @returns True when the study's external source provider is `c1c`.
 */
const isC1cStudy = (study: CardioOneStudy) => study.externalSource?.provider === C1C_PROVIDER;

/**
 * Tells whether a CardioOne-managed study runs on a device CardioOne supplies. Combines the already-resolved managed flag with the device-type check, so callers holding only the flag share one gate.
 *
 * @param isC1cProviderStudy - Whether the study comes from CardioOne (its external source provider is `c1c`).
 * @param deviceType - Study-level device type.
 * @returns True for a managed LifeSignals or VivaLNK study.
 */
const isManagedC1cDevice = (isC1cProviderStudy: boolean, deviceType: string | undefined) =>
  isC1cProviderStudy && formatC1cDeviceLabel(deviceType) !== null;

export { hasCardioOneFacilities, isC1cStudy, isCardioOneFacility, isManagedC1cDevice };
export type { C1cDeviceType };
